/**
import { apiClient } from '../../utils/apiHelper';
 * 🔒🔒🔒 LOCKED SYSTEM - DO NOT MODIFY WITHOUT USER AUTHORIZATION 🔒🔒🔒
 * 
 * STUDENT LEDGER COMPONENT
 * 
 * This component displays complete financial transaction history for students.
 * 
 * CRITICAL FEATURES (DO NOT MODIFY):
 * - Transaction type colors: red=charge, green=payment, blue=concession
 * - Filter functionality: "All Transactions", "Payments & Concessions", "Fee Charges Only"
 * - Multi-payment mode display support
 * - Entry user column
 * - Running balance calculation
 * 
 * See /app/STUDENT_LEDGER_LOCKED.md for complete documentation
 * 
 * ⚠️ ANY CHANGES REQUIRE USER AUTHORIZATION ⚠️
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Printer, User, Phone, Mail, BookOpen, Calendar, FileText } from 'lucide-react';
import { generateStudentLedgerPDF } from '../../utils/studentLedgerPrintGenerator';
import { studentsAPI } from '../../api/api';
import { formatDate } from '../../utils/dateTimeUtils';

const StudentLedger = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filterType, setFilterType] = useState('all');


  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await studentsAPI.getStudentsDropdown();
      const studentsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      
      const studentOptions = studentsData.map(student => ({
        value: student.id,
        label: `${student.full_name} (${student.admission_number})`,
        data: student
      }));
      
      setStudents(studentOptions);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchLedger = async (studentId, applyDateFilter = false) => {
    if (!studentId) return;
    
    setLoading(true);
    try {
      const params = {};
      if (applyDateFilter && fromDate) params.from_date = fromDate;
      if (applyDateFilter && toDate) params.to_date = toDate;
      
      const response = await studentsAPI.getLedger(studentId, params);
      setLedgerData(response.data);
    } catch (error) {
      console.error('Error fetching ledger:', error);
      alert('Failed to fetch ledger data');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentChange = (selectedOption) => {
    setSelectedStudent(selectedOption);
    if (selectedOption) {
      fetchLedger(selectedOption.value, false);
    } else {
      setLedgerData(null);
    }
  };

  const handleApplyFilter = () => {
    if (selectedStudent) {
      fetchLedger(selectedStudent.value, true);
    }
  };

  const handleClearFilter = () => {
    setFromDate('');
    setToDate('');
    setFilterType('all');
    if (selectedStudent) {
      fetchLedger(selectedStudent.value, false);
    }
  };

  const handlePrintLedger = () => {
    if (ledgerData) {
      generateStudentLedgerPDF(ledgerData);
    }
  };

  const getFilteredTransactions = () => {
    if (!ledgerData || !ledgerData.transactions) return [];
    
    if (filterType === 'all') return ledgerData.transactions;
    if (filterType === 'payments') return ledgerData.transactions.filter(t => t.type === 'payment' || t.type === 'concession');
    if (filterType === 'charges') return ledgerData.transactions.filter(t => t.type === 'fee_charge');
    
    return ledgerData.transactions;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const filteredTransactions = getFilteredTransactions();

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Student Ledger</h1>
        <p className="text-gray-600">View complete financial transaction history for students</p>
      </div>

      {/* Search and Filter Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Search Student</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Student
              </label>
              <Select
                value={selectedStudent}
                onChange={handleStudentChange}
                options={students}
                placeholder="Search by name or admission number..."
                isClearable
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Date
              </label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To Date
              </label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full"
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button onClick={handleApplyFilter} disabled={!selectedStudent}>
              Apply Filter
            </Button>
            <Button onClick={handleClearFilter} variant="outline">
              Clear Filter
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading ledger data...</p>
        </div>
      )}

      {!loading && ledgerData && (
        <>
          {/* Student Info Card */}
          <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 flex-1">
                  <div className="flex items-start gap-2">
                    <User className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Student Name</p>
                      <p className="font-semibold text-gray-900">{ledgerData.student.full_name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <FileText className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Admission Number</p>
                      <p className="font-semibold text-gray-900">{ledgerData.student.admission_number}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <BookOpen className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Course</p>
                      <p className="font-semibold text-gray-900">{ledgerData.student.course_name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <Calendar className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Session</p>
                      <p className="font-semibold text-gray-900">{ledgerData.student.session || 'N/A'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <Phone className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Mobile</p>
                      <p className="font-semibold text-gray-900">{ledgerData.student.mobile || 'N/A'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <Mail className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-semibold text-gray-900 text-sm">{ledgerData.student.email || 'N/A'}</p>
                    </div>
                  </div>
                </div>
                
                <Button onClick={handlePrintLedger} className="ml-4">
                  <Printer className="w-4 h-4 mr-2" />
                  Print Ledger
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Transaction Type Filter */}
          <div className="mb-4 flex gap-2">
            <Button
              onClick={() => setFilterType('all')}
              variant={filterType === 'all' ? 'default' : 'outline'}
              size="sm"
            >
              All Transactions
            </Button>
            <Button
              onClick={() => setFilterType('payments')}
              variant={filterType === 'payments' ? 'default' : 'outline'}
              size="sm"
            >
              Payments & Concessions
            </Button>
            <Button
              onClick={() => setFilterType('charges')}
              variant={filterType === 'charges' ? 'default' : 'outline'}
              size="sm"
            >
              Fee Charges Only
            </Button>
          </div>

          {/* Ledger Table */}
          <Card>
            <CardHeader>
              <CardTitle>Transaction History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-300">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Debit (₹)</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Credit (₹)</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Balance (₹)</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Payment Mode</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Receipt No</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Remarks</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Entry User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                          No transactions found
                        </td>
                      </tr>
                    ) : (
                      filteredTransactions.map((transaction, index) => (
                        <tr
                          key={index}
                          className={`border-b hover:bg-gray-50 ${
                            transaction.type === 'payment' ? 'bg-green-50' : 
                            transaction.type === 'concession' ? 'bg-blue-50' : 'bg-red-50'
                          }`}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formatDate(transaction.date)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                transaction.type === 'payment'
                                  ? 'bg-green-100 text-green-800'
                                  : transaction.type === 'concession'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {transaction.type === 'payment' ? 'Payment' : 
                               transaction.type === 'concession' ? 'Concession' : 'Fee Charge'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {transaction.description}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-red-600">
                            {transaction.debit > 0 ? formatCurrency(transaction.debit) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-green-600">
                            {transaction.credit > 0 ? formatCurrency(transaction.credit) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                            {formatCurrency(transaction.balance)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {transaction.payment_mode || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {transaction.receipt_number || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 max-w-xs">
                            {transaction.remarks || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {transaction.created_by_name || '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Summary Card */}
          <Card className="mt-6 bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardHeader>
              <CardTitle>Financial Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-sm text-gray-600 mb-1">Total Fee Charged</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(ledgerData.summary.total_debits)}
                  </p>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-sm text-gray-600 mb-1">Total Paid</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(ledgerData.summary.total_credits)}
                  </p>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-sm text-gray-600 mb-1">Outstanding Balance</p>
                  <p className={`text-2xl font-bold ${
                    ledgerData.summary.outstanding_balance > 0 ? 'text-orange-600' : 'text-green-600'
                  }`}>
                    {formatCurrency(ledgerData.summary.outstanding_balance)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {!loading && !ledgerData && selectedStudent && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p>No ledger data available for this student</p>
          </CardContent>
        </Card>
      )}

      {!loading && !selectedStudent && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <User className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p>Please select a student to view their ledger</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StudentLedger;